// src/design-system/Card.tsx
import clsx from "clsx";
import type { ComponentType } from "react";
import type { HTMLAttributes, ReactNode } from "react";

import { FaArrowDown, FaArrowUp } from "./faIcons";
import { STAT_CARD_THEME } from "./statCardTheme";
import type { Tone } from "./tone";

export function Card({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={clsx("glass", className)} {...rest} />;
}

/** بطاقة قسم بعنوان وإجراء اختياري في الرأس. */
export function Section({
  title, hint, action, children, className, flush,
}: { title?: ReactNode; hint?: ReactNode; action?: ReactNode; children: ReactNode; className?: string; flush?: boolean }) {
  return (
    <section className={clsx("glass-flat overflow-hidden", className)}>
      {(title || action) && (
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-3.5">
          <div className="flex items-baseline gap-3">
            {title && <h2 className="m-0 text-[15px] font-semibold text-gold-xl">{title}</h2>}
            {hint && <span className="text-xs text-muted-2">{hint}</span>}
          </div>
          {action}
        </header>
      )}
      <div className={flush ? "" : "p-5"}>{children}</div>
    </section>
  );
}

/** غلاف قسم بنفس زجاج/توهج التصميم المعتمد (خلفية متدرجة + شريط توهج 6px يمين) —
 * لتغليف بطاقات الرسوم البيانية بنفس هوية كروت المؤشرات، بلا تعديل على القيم الأصلية. */
export function GlowSection({
  tone = "gold", title, hint, children, className,
}: { tone?: Tone; title?: ReactNode; hint?: ReactNode; children: ReactNode; className?: string }) {
  const t = STAT_CARD_THEME[tone];
  return (
    <section
      className={clsx("relative flex flex-col gap-4 overflow-hidden p-6 sm:p-8", className)}
      style={{
        borderRadius: 20, backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
        background: t.cardBg, border: `1.5px solid ${t.cardBorder}`, boxShadow: t.cardShadow,
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute", top: 0, right: 0, width: 6, height: "100%",
          borderTopRightRadius: 20, borderBottomRightRadius: 20, zIndex: 10,
          background: t.stripGradient, boxShadow: t.stripGlow,
        }}
      />
      {(title || hint) && (
        <div>
          {title && <h2 className="m-0 text-lg font-bold tracking-wide text-white">{title}</h2>}
          {hint && <p className="mt-0.5 text-xs" style={{ color: "#94a3b8" }}>{hint}</p>}
        </div>
      )}
      {children}
    </section>
  );
}

export type StatTrend = { label: string; tone: Tone; dir?: "up" | "down" };
export type StatIcon = ComponentType<{ size?: number; className?: string }>;

/** بطاقة مؤشر (لوحة القيادة) — نسخة حرفية من تصميم fleet_analytics_dashboard.html المعتمد
 * من المستخدم بدون أي تعديل: خلفية بتدرّج قُطري ملوّن حسب الدرجة، شريط توهّج فعلي 6px
 * ملاصق للحافة اليمنى لطول الكارت بالكامل (`right:0` فعلي كما في الملف الأصلي، وليس
 * `border-inline-start` منطقي)، شارة أيقونة متوهجة `rounded-xl`، رقم ضخم `font-black` أبيض،
 * وسطر اتجاه ملوّن بسهم. هوية النظام (الذهبي/الكحلي) مستبعدة عمداً من هذا المكوّن فقط. */
export function StatCard({
  label, value, sub, tone = "gold", onClick, icon: Icon, trend,
}: { label: string; value: ReactNode; sub?: ReactNode; tone?: Tone; onClick?: () => void; icon?: StatIcon; trend?: StatTrend }) {
  const Wrapper = onClick ? "button" : "div";
  const dir = trend ? (trend.dir ?? (trend.label.trim().startsWith("-") ? "down" : "up")) : undefined;
  const t = STAT_CARD_THEME[tone];
  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className="relative flex flex-col justify-between overflow-hidden p-6 text-start"
      style={{
        cursor: onClick ? "pointer" : "default",
        borderRadius: 20,
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        background: t.cardBg,
        border: `1.5px solid ${t.cardBorder}`,
        boxShadow: t.cardShadow,
        transition: "transform .3s cubic-bezier(.4,0,.2,1)",
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute", top: 0, right: 0, width: 6, height: "100%",
          borderTopRightRadius: 20, borderBottomRightRadius: 20, zIndex: 10,
          background: t.stripGradient, boxShadow: t.stripGlow,
        }}
      />
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium tracking-wide" style={{ color: "#cbd5e1" }}>{label}</span>
        {Icon && (
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center"
            style={{ borderRadius: 12, background: t.iconBg, border: `1px solid ${t.iconBorder}`, color: t.iconColor, boxShadow: t.iconGlow }}
          >
            <Icon size={18} />
          </span>
        )}
      </div>
      <div className="my-3">
        <strong className="mono" style={{ fontSize: 30, lineHeight: 1.15, fontWeight: 900, letterSpacing: "-0.02em", color: "#fff", direction: "ltr", display: "block", textAlign: "start" }}>
          {value}
        </strong>
      </div>
      {(trend || sub) && (
        <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: t.iconColor, direction: "ltr" }}>
          {trend && (dir === "down" ? <FaArrowDown size={11} /> : <FaArrowUp size={11} />)}
          <span>{trend ? trend.label : sub}</span>
        </div>
      )}
    </Wrapper>
  );
}
